import { useState, useRef, useMemo, useLayoutEffect } from 'react'

import MoreLessToggle from 'ui-component-library/components/00-Primitives/MoreLessToggle'
import style from './style.scss'

const HEIGHT_BUFFER = 70

type ContentRenderProps = {
  onLoaded: () => void
}

type Props = {
  dateNavigation: React.ReactNode
  featuredContent?: (contentRenderProps: ContentRenderProps) => React.ReactNode
  workoutOfTheDay: React.ReactNode
  lessText: string
  moreText: string
}

function DailyModule({
  featuredContent,
  dateNavigation,
  workoutOfTheDay,
  lessText,
  moreText,
}: Props) {
  const workoutOfTheDayElement = useRef<HTMLDivElement>(null)
  const featuredContentElement = useRef<HTMLDivElement>(null)
  const [isWorkoutTooTall, setIsWorkoutTooTall] = useState(false)
  const [isWorkoutExpanded, setIsWorkoutExpanded] = useState(false)
  const [maxHeight, setMaxHeight] = useState<number>(0)

  function handleToggleExpanded() {
    setIsWorkoutExpanded((s) => !s)
  }

  function handleFeaturedContentLoaded() {
    if (featuredContentElement.current) {
      setMaxHeight(featuredContentElement.current.clientHeight)
    }
  }

  useLayoutEffect(() => {
    if (workoutOfTheDayElement.current) {
      setIsWorkoutTooTall(
        workoutOfTheDayElement.current.clientHeight > maxHeight + HEIGHT_BUFFER,
      )
    }
  }, [workoutOfTheDayElement.current, maxHeight])

  const height = useMemo(() => {
    if (isWorkoutTooTall && !isWorkoutExpanded) {
      return maxHeight
    }

    return 'auto'
  }, [isWorkoutTooTall, isWorkoutExpanded, maxHeight])

  const expandText = isWorkoutExpanded ? lessText : moreText

  return (
    <div className={style.wrapper}>
      {dateNavigation}
      <div className={style.content}>
        <div className={style.workoutOfTheDay}>
          <div
            ref={workoutOfTheDayElement}
            style={{ height }}
            className={style.workoutOfTheDayContent}
          >
            {workoutOfTheDay}
          </div>
          {isWorkoutTooTall && (
            <MoreLessToggle
              className={style.moreLessToggle}
              expandText={expandText}
              isExpanded={isWorkoutExpanded}
              onToggle={handleToggleExpanded}
            />
          )}
        </div>
        <span className={style.separator} />
        {featuredContent && (
          <div ref={featuredContentElement} className={style.featuredContent}>
            {featuredContent({ onLoaded: handleFeaturedContentLoaded })}
          </div>
        )}
      </div>
    </div>
  )
}

export default DailyModule
